/**
 * earlyErrorLogger — Pre-React boot-phase error capture.
 *
 * This module self-initializes on import (no function call needed).
 * It captures errors that happen BEFORE React mounts, when neither
 * ErrorBoundary nor App Insights are available yet.
 *
 * Captured errors are stored in localStorage and flushed to the
 * centralized errorStorage once React is ready.
 *
 * Import this file as early as possible in index.tsx — BEFORE React.
 *
 * Pattern proven in v1 (earlyErrorLogger.ts) and inspired by
 * Sentry's boot-phase SDK capture.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EarlyErrorKind =
  | "boot"
  | "window-error"
  | "unhandled-rejection"
  | "visibility-change"

interface EarlyLogEntry {
  kind: EarlyErrorKind
  timestamp: string
  message?: string
  stack?: string
  visibilityState?: string
  readyState?: string
  url?: string
  performanceNow?: number
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = "totodo_early_errors"
const MAX_ENTRIES = 30

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let initialized = false
const inMemoryQueue: EarlyLogEntry[] = []
let pendingFlush = false

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildEntry(kind: EarlyErrorKind, message?: string, stack?: string): EarlyLogEntry {
  return {
    kind,
    timestamp: new Date().toISOString(),
    message: message?.slice(0, 500),
    stack: stack?.slice(0, 1000),
    visibilityState: typeof document !== "undefined" ? document.visibilityState : undefined,
    readyState: typeof document !== "undefined" ? document.readyState : undefined,
    url: typeof window !== "undefined" ? window.location.href : undefined,
    performanceNow: typeof performance !== "undefined" ? Math.round(performance.now()) : undefined,
  }
}

function enqueue(entry: EarlyLogEntry): void {
  inMemoryQueue.push(entry)
  if (inMemoryQueue.length > MAX_ENTRIES) {
    inMemoryQueue.shift()
  }
  schedulePersist()
}

function schedulePersist(): void {
  if (pendingFlush) return
  pendingFlush = true
  // Use queueMicrotask to batch rapid-fire errors
  queueMicrotask(() => {
    persistToStorage()
    pendingFlush = false
  })
}

function persistToStorage(): void {
  try {
    const existing = readFromStorage()
    const combined = [...existing, ...inMemoryQueue]
    // Cap total entries
    const capped = combined.length > MAX_ENTRIES
      ? combined.slice(-MAX_ENTRIES)
      : combined
    localStorage.setItem(STORAGE_KEY, JSON.stringify(capped))
    inMemoryQueue.length = 0
  } catch {
    // localStorage unavailable — keep in memory
  }
}

function readFromStorage(): EarlyLogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

// ---------------------------------------------------------------------------
// Event handlers — registered before React mounts
// ---------------------------------------------------------------------------

function handleWindowError(event: ErrorEvent): void {
  enqueue(buildEntry(
    "window-error",
    event.message || String(event.error),
    event.error instanceof Error ? event.error.stack : undefined,
  ))
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason
  const message = reason instanceof Error ? reason.message : String(reason)
  const stack = reason instanceof Error ? reason.stack : undefined
  enqueue(buildEntry("unhandled-rejection", message, stack))
}

function handleVisibilityChange(): void {
  // Only log relevant transitions (hidden→visible can reveal wake-up errors)
  enqueue(buildEntry(
    "visibility-change",
    `visibility → ${document.visibilityState}`,
  ))
}

// ---------------------------------------------------------------------------
// Initialize — self-registers on module import
// ---------------------------------------------------------------------------

function initEarlyErrorLogger(): void {
  if (initialized || typeof window === "undefined") return
  initialized = true

  // Boot entry — proves the logger was loaded
  enqueue(buildEntry("boot", "Early error logger initialized"))

  // Register global handlers
  window.addEventListener("error", handleWindowError)
  window.addEventListener("unhandledrejection", handleUnhandledRejection)
  document.addEventListener("visibilitychange", handleVisibilityChange)
}

// Auto-initialize on module load
initEarlyErrorLogger()

// ---------------------------------------------------------------------------
// Flush API — called after React mounts to move early errors
// into the centralized errorStorage
// ---------------------------------------------------------------------------

/**
 * Flush early errors into the centralized errorStorage and clear.
 * Call this once after React mounts (e.g., in useAppWatchdog).
 */
export async function flushEarlyErrors(): Promise<void> {
  const entries = readFromStorage()
  if (entries.length === 0) return

  try {
    const { logError } = await import("./errorStorage")

    for (const entry of entries) {
      if (entry.kind === "boot") continue // Skip the boot marker

      logError(new Error(entry.message ?? "Unknown early error"), {
        source: "boot",
        severity: entry.kind === "window-error" || entry.kind === "unhandled-rejection"
          ? "error"
          : "info",
        data: {
          kind: entry.kind,
          performanceNow: entry.performanceNow ?? 0,
        },
      })
    }
  } catch {
    // errorStorage not available yet — keep in early storage
    return
  }

  // Clear early storage after successful flush
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Best effort
  }
}

/**
 * Get raw early error entries (for debug UI).
 */
export function getEarlyErrors(): EarlyLogEntry[] {
  return readFromStorage()
}
